/* import __COLOCATED_TEMPLATE__ from './prism-autocomplete.hbs'; */
import Component from '@ember/component';
import { isPresent, isEmpty } from '@ember/utils';
const KEY_CODE_ENTER = 13;
const KEY_CODE_ESC = 27;
const KEY_CODE_UP = 38;
const KEY_CODE_DOWN = 40;

// Chrome overrides the "off" input and continues to show the native autocomplete dropdown.
// Online forums suggest using "new-password" to stop autocomplete because this is
// typically used in new account forms to avoid autofilling other existing passwords.
const AUTOCOMPLETE_OFF = 'new-password';
const ROW_SELECTOR = '.prism-autocomplete__suggestions__row';
export default Component.extend({
  /**
   * The class to be assigned to the input field.
   *
   * @argument inputClass
   * @type {String}
   */
  inputClass: '',
  /**
   * The id to be assigned to the input field.
   *
   * @argument inputId
   * @type {String}
   */
  inputId: '',
  /**
   * The value to be assigned to the input field.
   *
   * @argument inputValue
   * @type {String}
   */
  inputValue: '',
  /**
   * The placeholder to be assigned to the input field.
   *
   * @argument inputPlaceholder
   * @type {String}
   */
  inputPlaceholder: '',
  /**
   * The max length to be assigned to the input field.
   *
   * @argument inputMaxLength
   * @type {String|Integer}
   */
  inputMaxLength: '',
  /**
   * Whether the input field is disabled or not.
   *
   * @argument inputIsDisabled
   * @type {Boolean}
   */
  inputIsDisabled: false,
  /**
   * Whether the browser can autocomplete the input.
   *
   * @argument inputBrowserAutocomplete
   * @type {String}
   */
  inputBrowserAutocomplete: AUTOCOMPLETE_OFF,
  /**
   * Suggestions to yield from the dropdown.
   *
   * @argument suggestions
   * @type {Array<any>}
   */
  suggestions: null,
  /**
   * The class to be assigned to the suggestions box.
   *
   * @argument suggestionsClass
   * @type {String}
   */
  suggestionsClass: '',
  /**
   * Action to call when a suggestion is selected. The suggestion object will be passed in as a parameter.
   *
   * @argument selectSuggestionAction
   * @type {Action}
   */
  selectSuggestionAction() {},
  /**
   * Action to call when the input is changed.
   *
   * @argument onChange
   * @type {Action}
   */
  onChange() {},
  highlightIndex: null,
  showSuggestions: false,
  selectSuggestion(...args) {
    this.selectSuggestionAction(...args);
    this.set('showSuggestions', false);
    this.set('highlightIndex', null);
  },
  highlightNext() {
    this.set('showSuggestions', true);
    if (this.highlightIndex === null) {
      this.set('highlightIndex', 0);
    } else {
      this.set('highlightIndex', Math.min(this.highlightIndex + 1, this.suggestions.length - 1));
    }
  },
  highlightPrevious() {
    this.set('showSuggestions', true);
    if (this.highlightIndex === null) {
      this.set('highlightIndex', this.suggestions.length - 1);
    } else {
      this.set('highlightIndex', Math.max(this.highlightIndex - 1, 0));
    }
  },
  enterPressed(e) {
    if (this.showSuggestions && isPresent(this.highlightIndex)) {
      this.selectSuggestion(this.suggestions[this.highlightIndex]);
      e.stopPropagation();
      e.preventDefault();
    }
  },
  escapePressed(e) {
    e.stopPropagation();
    e.preventDefault();
    this.set('highlightIndex', null);
    this.send('inputFocusOutAction');
  },
  keyDown(e) {
    if (isEmpty(this.suggestions)) {
      return;
    }
    switch (e.keyCode) {
      case KEY_CODE_DOWN:
        this.highlightNext(e);
        break;
      case KEY_CODE_UP:
        this.highlightPrevious(e);
        break;
      case KEY_CODE_ENTER:
        this.enterPressed(e);
        break;
      case KEY_CODE_ESC:
        this.escapePressed(e);
        break;
      default:
        this.set('highlightIndex', null);
        this.set('showSuggestions', true);
    }
  },
  actions: {
    handleMouseMove(event) {
      if (isEmpty(this.suggestions)) {
        return;
      }
      const target = event.target;
      const row = target && target.closest(ROW_SELECTOR);
      if (row) {
        // if mouse is moved inside a suggestion set the current highlight to the index of the suggestion
        const index = Number.parseInt(row.dataset.suggestionIndex, 10);
        this.set('highlightIndex', index);
      } else {
        // mouse if moved outside of a suggestion (e.g. into the input field)
        this.set('highlightIndex', null);
      }
    },
    handleMouseLeave() {
      this.set('highlightIndex', null);
    },
    inputFocusInAction() {
      this.set('showSuggestions', true);
    },
    inputFocusOutAction() {
      this.set('showSuggestions', false);
      this.set('highlightIndex', null);
    }
  },
  didInsertElement(...args) {
    this._super(...args);
    this.element.addEventListener('mouseMove', this.handleMouseMove);
    this.element.addEventListener('mouseLeave', this.handleMouseLeave);
  },
  willDestroyElement(...args) {
    this._super(...args);
    this.element.removeEventListener('mouseMove', this.handleMouseMove);
    this.element.removeEventListener('mouseLeave', this.handleMouseLeave);
  }
});